import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDiscoveryStore } from '../../../stores/useDiscoveryStore';
import { EXPLORE_IDEAS_LIST_PAGE_SIZE } from '../hooks/useExploreIdeasListPage';

export type IdeasListUiState = {
  page: number;
  selectedIdeaId?: string;
};

export function parseIdeasPageParam(rawValue: string | null): number {
  if (!rawValue) {
    return 1;
  }
  const parsed = Number.parseInt(rawValue, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function useExploreIdeasListArtifactUi(stateKey: string, artifactId?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlArtifactId = searchParams.get('artifactId')?.trim() ?? '';
  const urlIdeaId = searchParams.get('ideaId')?.trim() ?? '';
  const hasUrlIdeasPage = searchParams.has('ideasPage');
  const urlIdeasPage = parseIdeasPageParam(searchParams.get('ideasPage'));

  const storeUi = useDiscoveryStore((state) => state.ideasListUiByArtifactId[stateKey]);
  const setIdeasListUiForArtifact = useDiscoveryStore((state) => state.setIdeasListUiForArtifact);
  const setActiveArtifactId = useDiscoveryStore((state) => state.setActiveArtifactId);
  const setActiveArtifactRecordId = useDiscoveryStore((state) => state.setActiveArtifactRecordId);

  const resolvedArtifactId = artifactId?.trim() || undefined;
  const isActiveArtifact = Boolean(
    resolvedArtifactId && urlArtifactId === resolvedArtifactId
  );
  const isSelectionActive = resolvedArtifactId ? isActiveArtifact : !urlArtifactId;

  const page = isActiveArtifact ? urlIdeasPage : (storeUi?.page ?? 1);
  const selectedIdeaId = resolvedArtifactId
    ? (isActiveArtifact ? urlIdeaId : (storeUi?.selectedIdeaId ?? ''))
    : (isSelectionActive ? (urlIdeaId || storeUi?.selectedIdeaId || '') : (storeUi?.selectedIdeaId ?? ''));

  const didSeedStoreFromUrlRef = useRef(false);
  useEffect(() => {
    if (!isActiveArtifact || didSeedStoreFromUrlRef.current) {
      return;
    }
    didSeedStoreFromUrlRef.current = true;
    setIdeasListUiForArtifact(stateKey, {
      page: urlIdeasPage,
      selectedIdeaId: urlIdeaId || undefined,
    });
  }, [
    isActiveArtifact,
    setIdeasListUiForArtifact,
    stateKey,
    urlIdeasPage,
    urlIdeaId,
  ]);

  const onPageChange = useCallback(
    (nextPage: number) => {
      const safePage = Math.max(1, nextPage);
      setIdeasListUiForArtifact(stateKey, {
        page: safePage,
        selectedIdeaId: storeUi?.selectedIdeaId ?? (isActiveArtifact ? urlIdeaId : undefined),
      });

      if (!resolvedArtifactId) {
        return;
      }

      setActiveArtifactId(resolvedArtifactId);
      setActiveArtifactRecordId(resolvedArtifactId);

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentArtifactId = prev.get('artifactId')?.trim() ?? '';
          if (currentArtifactId && currentArtifactId !== resolvedArtifactId) {
            next.delete('ideaId');
          }
          next.set('artifactId', resolvedArtifactId);
          if (safePage > 1) {
            next.set('ideasPage', String(safePage));
          } else {
            next.delete('ideasPage');
          }
          return next;
        },
        { replace: true }
      );
    },
    [
      isActiveArtifact,
      resolvedArtifactId,
      setActiveArtifactId,
      setActiveArtifactRecordId,
      setIdeasListUiForArtifact,
      setSearchParams,
      stateKey,
      storeUi?.selectedIdeaId,
      urlIdeaId,
    ]
  );

  const rememberRowSelection = useCallback(
    (ideaId: string, targetPage: number) => {
      setIdeasListUiForArtifact(stateKey, {
        page: targetPage,
        selectedIdeaId: ideaId,
      });
    },
    [setIdeasListUiForArtifact, stateKey]
  );

  const computePageForIdeaId = useCallback(
    (ideaId: string, ideaIds: string[]) => {
      const selectedIndex = ideaIds.indexOf(ideaId);
      if (selectedIndex === -1) {
        return page;
      }
      return Math.floor(selectedIndex / EXPLORE_IDEAS_LIST_PAGE_SIZE) + 1;
    },
    [page]
  );

  return useMemo(
    () => ({
      page,
      selectedIdeaId,
      isActiveArtifact,
      isSelectionActive,
      hasUrlIdeasPage,
      onPageChange,
      rememberRowSelection,
      computePageForIdeaId,
    }),
    [
      computePageForIdeaId,
      hasUrlIdeasPage,
      isActiveArtifact,
      isSelectionActive,
      onPageChange,
      page,
      rememberRowSelection,
      selectedIdeaId,
    ]
  );
}
